
import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import StatusBadge from './StatusBadge';
import { Subscription } from '@/hooks/useCurrentSubscription';
import { SubscriptionDetails, SubscriptionActions } from './active-subscription';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface ActiveSubscriptionProps {
  subscription: Subscription;
}

const ActiveSubscription: React.FC<ActiveSubscriptionProps> = ({ subscription }) => {
  const { user, userProfile, refreshProfile } = useAuth();
  const [isSyncing, setIsSyncing] = useState(false);
  const queryClient = useQueryClient();
  
  // Vérifie si le niveau_compte dans userProfile est cohérent avec celui de l'abonnement
  const isLevelSynced = userProfile && subscription.niveau_compte && 
                        userProfile.niveau_compte_id === subscription.niveau_compte.id;
  
  // Fonction pour forcer la synchronisation de niveau de compte
  const syncAccountLevel = async () => {
    if (!user) return;
    
    try {
      setIsSyncing(true);
      toast.info("Synchronisation en cours...");
      
      const { data, error } = await supabase.functions.invoke('sync-user-level', {
        body: { userId: user.id, force: true }
      });
      
      if (error) {
        console.error("Erreur de synchronisation:", error);
        toast.error("Échec de la synchronisation");
        return;
      }
      
      if (data.success) {
        toast.success("Compte synchronisé avec succès");
        
        // Actualiser les données
        await Promise.all([
          refreshProfile(),
          queryClient.invalidateQueries({ queryKey: ['subscription'] })
        ]);
      } else {
        toast.warning("Synchronisation partielle", {
          description: "Actualisez la page pour voir les changements."
        });
      }
    } catch (err) {
      console.error("Exception lors de la synchronisation:", err);
      toast.error("Une erreur est survenue");
    } finally {
      setIsSyncing(false);
    }
  };
  
  // Effect pour vérifier automatiquement la synchronisation au chargement
  useEffect(() => {
    if (!isLevelSynced && user && subscription && !isSyncing) {
      console.log("Détection de désynchronisation:", {
        userProfileLevel: userProfile?.niveau_compte_id,
        subscriptionLevel: subscription.niveau_compte?.id
      });
      
      // Si non synchronisé, déclencher une synchronisation automatique
      syncAccountLevel();
    }
  }, [user?.id, subscription.id, isLevelSynced]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="text-xl">Votre abonnement</CardTitle>
          <CardDescription>Gérez votre plan et vos paiements</CardDescription>
        </div>
        <StatusBadge status={subscription.status} />
      </CardHeader>
      <CardContent className="space-y-4">
        <SubscriptionDetails subscription={subscription} />
        
        {!isLevelSynced && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
            <p className="font-medium">Synchronisation nécessaire</p>
            <p className="mt-1">Votre niveau de compte ne semble pas synchronisé avec votre abonnement.</p>
            <button 
              onClick={syncAccountLevel}
              disabled={isSyncing}
              className="mt-2 px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-md text-xs font-medium transition-colors"
            >
              {isSyncing ? "Synchronisation..." : "Synchroniser maintenant"}
            </button>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <SubscriptionActions 
          stripeCustomerId={subscription.stripe_customer_id} 
          stripeSubscriptionId={subscription.stripe_subscription_id} 
        />
      </CardFooter>
    </Card>
  );
};

export default ActiveSubscription;
